<script setup lang="ts">
import ChatDiagnosisTemplateQuestions from './ChatDiagnosisTemplateQuestions.vue';
import { useGetStoredDiagnosisAnswers } from './composables/useStoredDiagnosisAnswers';
import BotChatMessageLayout from '@/components/organisms/ChatMessage/layouts/BotChatMessageLayout.vue';
import { graphql } from '@/gql';
import ButtonFlat from 'theme/components/atoms/ButtonFlat.vue';
import SectionContainer from 'theme/components/molecules/SectionContainer.vue';
import SpeechBalloon from 'theme/components/molecules/SpeechBalloon.vue';

const { logger } = useLogger();

const hasAnsweredContinue = ref(false);
const { initialAnswers, clearInitialAnswers } = useGetStoredDiagnosisAnswers();
const shouldShowContinueDialog = computed(
  () => initialAnswers.value !== undefined && !hasAnsweredContinue.value
);

onMounted(() => {
  if (initialAnswers.value === undefined) {
    logger.value?.event.startDiagnosis();
  }
});

const { data: queryResult } = useAuthedQuery(
  graphql(`
    query ChatDiagnosisTemplateViewer {
      viewer2 {
        ... on ViewerQuerySuccessPayload {
          result {
            id
          }
        }
        ...ChatDiagnosisTemplateQuestionsViewer
      }
    }
  `),
  {},
  { apollo: { fetchPolicy: 'no-cache' } }
);

const { data: jobsQueryResult } = await useAsyncQuery(
  graphql(`
    query ChatDiagnosisTemplateJobs {
      jobs {
        ...ChatDiagnosisTemplateQuestionsJobs
      }
    }
  `),
  {},
  { apollo: { fetchPolicy: 'no-cache' } }
);

const viewer = computed(() => queryResult.value?.viewer2);

const jobs = computed(() => jobsQueryResult.value?.jobs);

const handleContinueDiagnose = (isContinue: boolean) => {
  hasAnsweredContinue.value = true;
  if (!isContinue) {
    clearInitialAnswers();
    logger.value?.event.restartDiagnosis();
    return;
  }
  logger.value?.event.resumeDiagnosis();
};
</script>

<template>
  <SectionContainer max-variant="small">
    <main :class="$style.page">
      <BotChatMessageLayout v-if="shouldShowContinueDialog">
        <template #message>
          <SpeechBalloon>
            <p>以前の続きから始めますかにゃ？</p>
          </SpeechBalloon>
        </template>
        <template #attachment>
          <ul :class="$style.selections">
            <li :class="$style.selections__item">
              <ButtonFlat
                :class="$style.button"
                @click="handleContinueDiagnose(true)"
              >
                はい
              </ButtonFlat>
            </li>
            <li :class="$style.selections__item">
              <ButtonFlat
                :class="$style.button"
                @click="handleContinueDiagnose(false)"
              >
                いいえ
              </ButtonFlat>
            </li>
          </ul>
        </template>
      </BotChatMessageLayout>
      <ChatDiagnosisTemplateQuestions
        v-if="!shouldShowContinueDialog && jobs"
        :viewer="viewer"
        :jobs="jobs"
        :initial-answers="initialAnswers"
      />
    </main>
  </SectionContainer>
</template>

<style lang="scss" module>
.page {
  padding-top: 40px;
  padding-bottom: 40px;
}

.selections {
  display: flex;
  align-items: center;
  gap: 8px;

  &__item {
    flex: 1 0 0;
  }
}

.button {
  width: 100%;
}
</style>
